import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Slide,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useAuthStore } from "../store";
import { useTheme, useMediaQuery } from "@mui/material";

const StoreModal = ({ open, onClose, lessonCategoryId, lessonId }) => {
  // Added lessonCategory as a prop
  const [availablePasses, setAvailablePasses] = useState([]);
  const [selectedPass, setSelectedPass] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [addedPass, setAddedPass] = useState(null);
  const [acceptRegulamin, setAcceptRegulamin] = useState(false);

  const { user } = useAuthStore((state) => ({
    user: state.user,
  }));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (open) {
      fetchAvailablePasses();
    }
  }, [open]);

  // Helper function to check if a pass is unlimited
  const isUnlimitedPass = (pass) => {
    return pass.lesson_categories_for_pass_type.some(
      (category) => category.is_unlimited === true
    );
  };

  const fetchAvailablePasses = async () => {
    setLoading(true);
    const idToken = await user.getIdToken();
    try {
      // Use the new endpoint with availability info
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/with-availability-info`,
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );
      const data = await response.json();
      if (Array.isArray(data.objects)) {
        let passes = data.objects;

        // Filter passes by lesson category if provided
        if (lessonCategoryId) {
          passes = passes.filter((pass) =>
            pass.lesson_categories_for_pass_type.some(
              (lesson) => lesson.lesson_category_id === lessonCategoryId
            )
          );
        }

        // Filter out passes that are not available due to purchase limits
        passes = passes.filter(pass => 
          !pass.availability_info || pass.availability_info.is_available
        );

        setAvailablePasses(passes);
      } else {
        console.error("Invalid passes data format", data);
        setAvailablePasses([]);
      }
    } catch (error) {
      console.error("Failed to fetch available passes", error);
      // Fallback to original endpoint for backward compatibility
      try {
        const fallbackResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/available?respect_availability=true`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
        const fallbackData = await fallbackResponse.json();
        if (Array.isArray(fallbackData.objects)) {
          let passes = fallbackData.objects;

          if (lessonCategoryId) {
            passes = passes.filter((pass) =>
              pass.lesson_categories_for_pass_type.some(
                (lesson) => lesson.lesson_category_id === lessonCategoryId
              )
            );
          }

          setAvailablePasses(passes);
        } else {
          setAvailablePasses([]);
        }
      } catch (fallbackError) {
        console.error("Fallback fetch also failed", fallbackError);
        setAvailablePasses([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (pass) => {
    setAddedPass(pass);
    setTimeout(() => {
      setSelectedPass(pass);
      setAddedPass(null);
    }, 300);
  };

  const handleRemoveFromCart = () => {
    setSelectedPass(null);
  };

  const handleSubmit = async () => {
    if (!acceptRegulamin) {
      alert("Proszę zaakceptować Regulamin.");
      return;
    }

    setPurchaseLoading(true);
    const idToken = await user.getIdToken();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/payment/create_order`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            pass_type_name: selectedPass.name,
            lesson_id: lessonId,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        
        // Handle purchase limit exceeded error
        if (errorData && errorData.detail && typeof errorData.detail === 'object' && errorData.detail.error_type === 'purchase_limit_exceeded') {
          const { pass_type_name, current_count, limit } = errorData.detail;
          alert(`Limit zakupów został osiągnięty!\n\nKarnet "${pass_type_name}" ma limit ${limit} aktywnych karnetów.\nAktualnie aktywnych: ${current_count}\n\nSpróbuj ponownie później lub wybierz inny karnet.`);
        } else if (errorData && errorData.detail && typeof errorData.detail === 'string') {
          alert(`Błąd podczas zakupu: ${errorData.detail}`);
        } else {
          throw new Error("Failed to create order");
        }
        return;
      }

      const data = await response.json();
      const redirectUrl = data;

      // Show alert and then redirect
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Failed to create order", error);
      alert("Wystąpił błąd podczas procesu zakupu. Spróbuj ponownie.");
    } finally {
      setPurchaseLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : "50%", // Adjust width based on device type
          maxHeight: "80%",
          overflowY: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" gutterBottom>
          Kup karnet
        </Typography>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {(() => {
              // Split passes into unlimited and regular
              const unlimitedPasses = availablePasses.filter(isUnlimitedPass);
              const regularPasses = availablePasses.filter(pass => !isUnlimitedPass(pass));

              return (
                <Box style={{ maxHeight: "60vh", overflowY: "auto" }}>
                  {/* Unlimited passes section */}
                  {unlimitedPasses.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                        Karnety bez limitu
                      </Typography>
                      <Grid container spacing={2}>
                        {unlimitedPasses.map((pass) => (
                          <Grid item xs={12} sm={6} key={pass.id}>
                            <Slide
                              direction={addedPass?.id === pass.id ? "left" : "up"}
                              in={true}
                              mountOnEnter
                              unmountOnExit
                              timeout={{ enter: 300, exit: 300 }}
                            >
                              <Card>
                                <CardContent>
                                  <Typography variant="h6">{pass.name}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Cena: {pass.price} zł
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Czas trwania: Bez limitu
                                  </Typography>

                                  {/* Show availability information */}
                                  {pass.month_restricted && pass.available_months && (() => {
                                    const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-11, so add 1
                                    const futureMonths = pass.available_months.filter(month => month > currentMonth);
                                    
                                    return futureMonths.length > 0 ? (
                                      <Typography variant="body2" color="primary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                        <strong>Karnet aktywny w miesiącach:</strong> {futureMonths.map(month => {
                                          const monthNames = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
                                                            "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
                                          return monthNames[month - 1];
                                        }).join(", ")}
                                      </Typography>
                                    ) : null;
                                  })()}

                                  {pass.purchase_period_restricted && (pass.purchase_available_from || pass.purchase_available_until) && (
                                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                      {pass.purchase_available_from && pass.purchase_available_until && (
                                        <><strong>Można kupić:</strong> {new Date(pass.purchase_available_from).toLocaleDateString()} - {new Date(pass.purchase_available_until).toLocaleDateString()}</>
                                      )}
                                      {pass.purchase_available_from && !pass.purchase_available_until && (
                                        <><strong>Można kupić od:</strong> {new Date(pass.purchase_available_from).toLocaleDateString()}</>
                                      )}
                                      {!pass.purchase_available_from && pass.purchase_available_until && (
                                        <><strong>Można kupić do:</strong> {new Date(pass.purchase_available_until).toLocaleDateString()}</>
                                      )}
                                    </Typography>
                                  )}

                                  {/* Show purchase limit information */}
                                  {pass.availability_info && pass.availability_info.has_purchase_limit && (
                                    <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontStyle: 'italic' }}>
                                      <strong>Limit:</strong> {pass.availability_info.remaining_slots} z {pass.availability_info.purchase_limit} dostępnych
                                    </Typography>
                                  )}
                                  {selectedPass && selectedPass.id === pass.id ? (
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      fullWidth
                                      onClick={handleRemoveFromCart}
                                    >
                                      Usuń z koszyka
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      fullWidth
                                      onClick={() => handleAddToCart(pass)}
                                      disabled={selectedPass !== null}
                                    >
                                      Dodaj do koszyka
                                    </Button>
                                  )}
                                </CardContent>
                              </Card>
                            </Slide>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {/* Regular passes section */}
                  {regularPasses.length > 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        Standardowe karnety
                      </Typography>
                      <Grid container spacing={2}>
                        {regularPasses.map((pass) => (
                          <Grid item xs={12} sm={6} key={pass.id}>
                            <Slide
                              direction={addedPass?.id === pass.id ? "left" : "up"}
                              in={true}
                              mountOnEnter
                              unmountOnExit
                              timeout={{ enter: 300, exit: 300 }}
                            >
                              <Card>
                                <CardContent>
                                  <Typography variant="h6">{pass.name}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Cena: {pass.price} zł
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Czas trwania: {Math.floor(pass.valid_duration / 86400)}{" "}
                                    dni
                                  </Typography>

                                  {/* Show availability information */}
                                  {pass.month_restricted && pass.available_months && (() => {
                                    const currentMonth = new Date().getMonth() + 1; // getMonth() returns 0-11, so add 1
                                    const futureMonths = pass.available_months.filter(month => month > currentMonth);
                                    
                                    return futureMonths.length > 0 ? (
                                      <Typography variant="body2" color="primary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                        <strong>Karnet aktywny w miesiącach:</strong> {futureMonths.map(month => {
                                          const monthNames = ["Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
                                                            "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"];
                                          return monthNames[month - 1];
                                        }).join(", ")}
                                      </Typography>
                                    ) : null;
                                  })()}

                                  {pass.purchase_period_restricted && (pass.purchase_available_from || pass.purchase_available_until) && (
                                    <Typography variant="body2" color="primary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                      {pass.purchase_available_from && pass.purchase_available_until && (
                                        <><strong>Można kupić:</strong> {new Date(pass.purchase_available_from).toLocaleDateString()} - {new Date(pass.purchase_available_until).toLocaleDateString()}</>
                                      )}
                                      {pass.purchase_available_from && !pass.purchase_available_until && (
                                        <><strong>Można kupić od:</strong> {new Date(pass.purchase_available_from).toLocaleDateString()}</>
                                      )}
                                      {!pass.purchase_available_from && pass.purchase_available_until && (
                                        <><strong>Można kupić do:</strong> {new Date(pass.purchase_available_until).toLocaleDateString()}</>
                                      )}
                                    </Typography>
                                  )}

                                  {/* Show purchase limit information */}
                                  {pass.availability_info && pass.availability_info.has_purchase_limit && (
                                    <Typography variant="body2" color="warning.main" sx={{ mt: 1, fontStyle: 'italic' }}>
                                      <strong>Limit:</strong> {pass.availability_info.remaining_slots} z {pass.availability_info.purchase_limit} dostępnych
                                    </Typography>
                                  )}
                                  {selectedPass && selectedPass.id === pass.id ? (
                                    <Button
                                      variant="contained"
                                      color="secondary"
                                      fullWidth
                                      onClick={handleRemoveFromCart}
                                    >
                                      Usuń z koszyka
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      fullWidth
                                      onClick={() => handleAddToCart(pass)}
                                      disabled={selectedPass !== null}
                                    >
                                      Dodaj do koszyka
                                    </Button>
                                  )}
                                </CardContent>
                              </Card>
                            </Slide>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </>
        )}
        <Box
          mt={3}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
        >
          <Box display="flex" justifyContent="space-between" width="100%">
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptRegulamin}
                  onChange={(e) => setAcceptRegulamin(e.target.checked)}
                  name="acceptRegulamin"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Akceptuję{" "}
                  <a
                    href={`${process.env.PUBLIC_URL}/statute`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Regulamin
                  </a>
                </Typography>
              }
            />
            <Button variant="contained" color="error" onClick={onClose}>
              Anuluj
            </Button>
          </Box>
          <Box display="flex" justifyContent="flex-end" width="100%" mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={purchaseLoading || !selectedPass || !acceptRegulamin}
            >
              {purchaseLoading ? (
                <CircularProgress size={24} />
              ) : (
                "Kup wybrany karnet"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default StoreModal;
